/* Adaptador CLIENTE de live.european-athletics.com (European Athletics).
 *
 * El API tRPC (api.european-athletics.com) es público y con CORS abierto
 * (`access-control-allow-origin: *`), pero Cloudflare BLOQUEA las IPs de
 * datacenter (2026-07-28: la Cloud Function en GCP recibe el muro; un
 * navegador residencial pasa). Por eso la fuente "ea" se scrapea AQUÍ, en el
 * navegador del admin, y no en /api/events. Réplica en TS de
 * functions/podios/scraper_ea.py (referencia y documentación del API allí).
 *
 * Keys propias: "EA:<competitionCode>:<unitId>".
 */

import type { EventsResponse, EventDetail, PodioRunner, Evento, Intento } from '@/lib/api';

const API = 'https://api.european-athletics.com/trpc/';

async function trpc<T>(proc: string, payload: Record<string, string>): Promise<T> {
  const u = `${API}${proc}?input=${encodeURIComponent(JSON.stringify({ json: payload }))}`;
  const r = await fetch(u);
  if (!r.ok) throw new Error(`${proc} ${r.status}`);
  return (await r.json()).result.data.json as T;
}

export function esUrlEA(url: string): boolean {
  try {
    return new URL(url).hostname.endsWith('european-athletics.com');
  } catch {
    return false;
  }
}

const ESTADOS: Record<string, string> = {
  official: 'Oficial', unofficial: 'Extraoficial', inprogress: 'Empezada',
  running: 'Empezada', live: 'Empezada', started: 'Empezada',
  scheduled: 'Lista de Salida', startlist: 'Lista de Salida',
  cancelled: 'Cancelada', postponed: 'Aplazada',
};
const FASES: Record<string, string> = {
  RND1: 'Eliminatoria', RND2: '2ª Ronda', HEAT: 'Eliminatoria', SFNL: 'Semifinal',
  QFNL: 'Cuartos de final', 'FNL-': 'Final', FNL: 'Final', QUAL: 'Clasificación',
};
const IRM = new Set(['DNS', 'DNF', 'DQ', 'DSQ', 'NM', 'NH']);

function estadoES(s: string): string {
  return ESTADOS[(s || '').toLowerCase().replace(/[^a-z]/g, '')] ?? (s || '');
}

function fechaHora(iso: string): [string, string] {
  const m = /(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(iso || '');
  return m ? [`${m[3]}/${m[2]}/${m[1]}`, m[4]] : ['', ''];
}

/** "rieti-2026" no se resuelve por API: se prueban códigos por año contra
 *  getScheduleFeed (override con ?code=XXXX en la URL pegada).
 *  Semillas: slugs cuyo sondeo resolvería MAL — "birmingham-2026" (ECH26)
 *  matchearía antes EU18CH26 (Rieti), que también existe en 2026. */
const slugCode: Record<string, string> = { 'birmingham-2026': 'ECH26' };

async function codigoDeUrl(url: string): Promise<string> {
  const u = new URL(url);
  const forzado = u.searchParams.get('code');
  if (forzado) return forzado.toUpperCase();
  const slug = u.pathname.split('/').filter(Boolean)[0] ?? '';
  if (slugCode[slug]) return slugCode[slug];
  const yy = (/(\d{4})/.exec(slug)?.[1] ?? `${new Date().getFullYear()}`).slice(2);
  for (const serie of ['EU18CH', 'EU20CH', 'EU23CH', 'ECH', 'EICH', 'ETCH', 'ECPCH', 'EXCH']) {
    try {
      const d = await trpc<{ events?: unknown[] }>('liveResults.getScheduleFeed', { competitionCode: `${serie}${yy}` });
      if (d.events?.length) {
        slugCode[slug] = `${serie}${yy}`;
        return slugCode[slug];
      }
    } catch { /* siguiente candidato */ }
  }
  throw new Error(`No se pudo resolver el código de competición de '${slug}' (añade ?code=XXXX a la URL)`);
}

interface EAUnit { id: string; name: string; status: string; scheduledStartTime?: string }
// `showSummary` / `status: "Finished"` son la señal de EA de que la RONDA está
// resuelta: es cuando publica la pestaña SUMMARY con los clasificados, incluidos
// los que pasan por tiempo (q). Verificado 2026-08-10 con el 800 m Hombres de
// ECH26 ya cerrado: la fase RND1 traía `showSummary: true` + `status:
// "Finished"`, mientras que semifinales y final seguían en "Scheduled".
// El motor del bot lo necesita para saber cuándo puede confirmar la repesca
// (ver auto_ea_seguimiento._cerrar_rondas): el estado de cada unidad por
// separado no dice si EA ya ha resuelto la ronda entera.
interface EAPhase {
  scheduledStartTime?: string; units?: EAUnit[];
  showSummary?: boolean; status?: string;
}
interface EAHeader { event?: { id: string; name: string; phases?: EAPhase[] } }

function faseES(phaseId: string, unitName: string): string {
  const seg = (phaseId || '').slice(22, 26);
  const base = FASES[seg] ?? FASES[seg.replace(/-+$/, '')] ?? seg.replace(/-+$/, '') ?? 'Final';
  const m = /(?:Heat|Group)\s+(\w+)$/.exec(unitName || '');
  return m ? `${base} ${m[1]}` : base;
}

/** EventsResponse compatible con /api/events, construido en el navegador. */
export async function fetchEventsEA(url: string): Promise<EventsResponse> {
  const code = await codigoDeUrl(url);
  const sch = await trpc<{ events?: { eventId: string }[] }>('liveResults.getScheduleFeed', { competitionCode: code });
  const ids = (sch.events ?? []).map((e) => e.eventId);
  const headers: (EAHeader | null)[] = [];
  // Lotes PEQUEÑOS (3) + reintento con backoff: sin la cookie __cf_bm (un
  // fetch cross-site sin credenciales no puede enviarla), Cloudflare corta
  // las ráfagas grandes aunque deje pasar peticiones sueltas (2026-07-28).
  const header = async (eid: string): Promise<EAHeader | null> => {
    for (let intento = 0; intento < 2; intento++) {
      try {
        return await trpc<EAHeader>('liveResults.getEventResultsHeaderFeed', { event: eid.toLowerCase(), competitionCode: code });
      } catch (err) {
        if (intento === 0) await new Promise((res) => setTimeout(res, 500));
        else console.warn('[eaLive] header feed falló', eid, err);
      }
    }
    return null;
  };
  for (let i = 0; i < ids.length; i += 3) {
    headers.push(...await Promise.all(ids.slice(i, i + 3).map(header)));
  }
  const eventos: Evento[] = [];
  for (const h of headers) {
    const ev = h?.event;
    if (!ev?.id) continue;
    const nombre = (ev.name || '').replace('Women', 'Mujeres').replace('Men', 'Hombres');
    for (const ph of ev.phases ?? []) {
      const units = ph.units ?? [];
      units.forEach((u, i) => {
        const [fecha, hora] = fechaHora(u.scheduledStartTime || ph.scheduledStartTime || '');
        let ronda = faseES(u.id || '', u.name || '');
        if (units.length > 1 && !ronda.includes('/')) {
          ronda = `${ronda.replace(/[0-9 ]+$/, '')} ${i + 1}/${units.length}`.replace('  ', ' ');
        }
        eventos.push({
          pruebaId: `EA:${code}:${u.id}`, detalleKey: `EA:${code}:${u.id}`,
          nombre, ronda, fecha, hora, estado: estadoES(u.status || ''), viento: '',
          podio: [], resultados: [],
          // Señal de RONDA RESUELTA (ver interface EAPhase). Va en cada unidad
          // porque el espejo transporta unidades, no fases.
          resumen: ph.showSummary === true || ph.status === 'Finished',
        } as Evento);
      });
    }
  }
  const nombres: Record<string, string> = {
    EU18CH: 'Campeonato de Europa Sub-18', EU20CH: 'Campeonato de Europa Sub-20',
    EU23CH: 'Campeonato de Europa Sub-23', ECH: 'Campeonato de Europa',
    EICH: 'Campeonato de Europa en Pista Cubierta',
  };
  const anio = /20\d{2}/.exec(url)?.[0] ?? '';
  return {
    competicion: `${nombres[code.slice(0, -2)] ?? code}${anio ? ` ${anio}` : ''}`,
    fecha: '', sede: null, fuente: 'ea', eventos,
  } as EventsResponse;
}

// Diccionario de atletas por competición (los feeds solo traen athleteId).
const atletasCache: Record<string, Record<string, { nombre: string; pais: string | null }>> = {};

// Pruebas (eventId[:22]) con algún ESPAÑOL inscrito, por competición.
const espEventosCache: Record<string, Set<string>> = {};

async function atletas(code: string) {
  if (atletasCache[code]) return atletasCache[code];
  const d = await trpc<{ athletes?: { athleteId: string; firstName?: string; lastName?: string; countryCode?: string; entries?: { eventId?: string }[] }[] }>(
    'liveResults.getAthletesFeed', { competitionCode: code });
  const esp = new Set<string>();
  for (const a of d.athletes ?? []) {
    if ((a.countryCode ?? '').trim() === 'ESP') {
      for (const e of a.entries ?? []) if (e.eventId) esp.add(e.eventId.slice(0, 22).toUpperCase());
    }
  }
  espEventosCache[code] = esp;
  const out: Record<string, { nombre: string; pais: string | null }> = {};
  for (const a of d.athletes ?? []) {
    const apellido = (a.lastName ?? '').trim().toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
    out[String(a.athleteId)] = {
      nombre: `${(a.firstName ?? '').trim()} ${apellido}`.trim(),
      pais: (a.countryCode ?? '').trim() || null,
    };
  }
  atletasCache[code] = out;
  return out;
}

interface EAAthlete {
  athleteId?: string; bib?: string; rank?: string; rankOrder?: number; result?: string;
  bestResultWind?: string; medal?: string; recordFlags?: string; records?: unknown[];
  qualificationMark?: string;
  attempts?: { height?: string; attempts?: string; results?: string }[];
}

/** EventDetail compatible con /api/event-detail para keys "EA:...". */
export async function fetchEventDetailEA(key: string): Promise<EventDetail> {
  const m = /^EA:([^:]+):(.+)$/.exec(key);
  const code = m?.[1] ?? '';
  const unitId = m?.[2] ?? '';
  const vacio = {
    intentos: {}, combinada: {}, clasificacion: [], salida: [], alineacion: [],
    noClasificados: [], qualiByRank: null, qualiByTime: null, avisos: {}, avisosDorsal: {}, records: {},
  } as unknown as EventDetail;
  if (!code || !unitId) return vacio;
  const [d, dicc] = await Promise.all([
    trpc<{ athletes?: EAAthlete[] }>('liveResults.getRaceResultsFeed', { event: unitId.toLowerCase(), competitionCode: code }),
    atletas(code),
  ]);
  const medallas: Record<string, string> = { gold: '1', silver: '2', bronze: '3' };
  const clasificacion: PodioRunner[] = [];
  const noClasificados: EventDetail['noClasificados'] = [];
  const intentos: Record<string, Intento[]> = {};
  const records: Record<string, string[]> = {};
  const orden = [...(d.athletes ?? [])].sort((a, b) => (a.rankOrder ?? 999) - (b.rankOrder ?? 999));
  for (const a of orden) {
    const aid = String(a.athleteId ?? '');
    const info = dicc[aid] ?? { nombre: aid, pais: null };
    const resultado = (a.result ?? '').trim();
    // Serie de intentos. En EA un intento PENDIENTE llega como {attemptId:N}
    // SIN `results` → mark '' (la tarjeta lo pinta como casilla vacía, no '-');
    // un nulo llega como results:'x' → 'X'; una marca válida, la marca. En
    // vertical (altura/pértiga) el valor va en `attempts`, no en `results`.
    const serie: Intento[] = (a.attempts ?? []).map((at) => at.height
      ? { height: String(at.height), mark: (at.attempts ?? '').trim().toUpperCase() }
      : { height: '', mark: (at.results ?? '').trim().toUpperCase() });
    const haCompetido = serie.some((s) => (s.mark ?? '').trim() !== '');
    // DNS/IRM SOLO si es un IRM EXPLÍCITO, o el atleta no ha competido nada.
    // Un atleta con intentos hechos pero aún sin marca válida (p.ej. nulo en
    // el 1er lanzamiento, caso Beregszászi 2026-08-10) NO es DNS: compite, va
    // a la clasificación con la marca en blanco y sus intentos.
    if (IRM.has(resultado.toUpperCase()) || (!resultado && !haCompetido)) {
      noClasificados.push({
        nombre: info.nombre, club: info.pais ?? '', estado: resultado.toUpperCase() || 'DNS',
        dorsal: (a.bib ?? '').trim() || null, categoria: null,
      } as EventDetail['noClasificados'][number]);
      continue;
    }
    clasificacion.push({
      puesto: String(a.rank ?? ''), nombre: info.nombre, atleta: info.nombre,
      club: info.pais ?? '', marca: resultado, viento: (a.bestResultWind ?? '').trim(),
      pais: info.pais, athId: aid, dorsal: (a.bib ?? '').trim() || null,
      categoria: null, quali: (a.qualificationMark ?? '').trim() || null, esGuest: false,
      medalla: medallas[(a.medal ?? '').toLowerCase()] ?? null, etiquetas: [],
    } as PodioRunner);
    const flags = (a.recordFlags ?? '').split(',').map((f) => f.trim()).filter(Boolean);
    if (flags.length) records[aid] = flags;
    if (serie.length) intentos[aid] = serie;
  }
  return {
    ...vacio, clasificacion, noClasificados, intentos, records,
    alineacion: orden.map((a) => ({ athId: String(a.athleteId ?? ''), nombre: (dicc[String(a.athleteId ?? '')] ?? {}).nombre ?? '' })),
  } as EventDetail;
}

/** Filtro de ESPAÑOLES (2026-07-29): mapa key→habilitada para el /telegram.
 *  Regla: prueba sin ESP inscrito → todas sus unidades deshabilitadas; con
 *  ESP → la PRIMERA ronda entera sí, y cada ronda posterior solo si algún
 *  ESP aparece en ella (siguen vivos). Las rondas posteriores exigen mirar
 *  el detalle de sus unidades → lotes de 3 con el `detalleDe` cacheado del
 *  llamante; si una unidad aún no tiene datos, se deja habilitada (se
 *  reevalúa al recargar). */
export async function espPorUnidadEA(
  eventos: { detalleKey?: string | null; fecha?: string; hora?: string }[],
  detalleDe: (key: string) => Promise<EventDetail>,
): Promise<Record<string, boolean>> {
  const out: Record<string, boolean> = {};
  const code = /^EA:([^:]+):/.exec(eventos.find((e) => e.detalleKey)?.detalleKey ?? '')?.[1];
  if (!code) return out;
  await atletas(code);
  const esp = espEventosCache[code] ?? new Set<string>();
  // Agrupar por prueba (eventId[:22]) y por fase (unitId[22:26]).
  const porEvento = new Map<string, Map<string, { key: string; ts: string }[]>>();
  for (const e of eventos) {
    const m = /^EA:[^:]+:(.+)$/.exec(e.detalleKey ?? '');
    if (!m) continue;
    const uid = m[1].toUpperCase();
    const ev = uid.slice(0, 22);
    const fase = uid.slice(22, 26);
    if (!porEvento.has(ev)) porEvento.set(ev, new Map());
    const fases = porEvento.get(ev)!;
    if (!fases.has(fase)) fases.set(fase, []);
    const [d, mm, y] = (e.fecha ?? '').split('/');
    fases.get(fase)!.push({ key: e.detalleKey!, ts: `${y ?? ''}${mm ?? ''}${d ?? ''}${e.hora ?? ''}` });
  }
  const pendientes: { key: string }[] = [];
  for (const [ev, fases] of porEvento) {
    if (!esp.has(ev)) {
      for (const us of fases.values()) for (const u of us) out[u.key] = false;
      continue;
    }
    const orden = [...fases.entries()].sort(
      (a, b) => (a[1][0]?.ts ?? '').localeCompare(b[1][0]?.ts ?? ''));
    orden.forEach(([, us], i) => {
      if (i === 0) for (const u of us) out[u.key] = true;
      else for (const u of us) pendientes.push({ key: u.key });
    });
  }
  // Rondas posteriores: ESP presente en ALGUNA unidad de la ronda → toda la
  // ronda habilitada. Se resuelve unidad a unidad y luego se propaga.
  const tieneEsp: Record<string, boolean> = {};
  for (let i = 0; i < pendientes.length; i += 3) {
    await Promise.all(pendientes.slice(i, i + 3).map(async ({ key }) => {
      try {
        const det = await detalleDe(key);
        const filas = [...det.clasificacion, ...(det.noClasificados ?? [])];
        tieneEsp[key] = filas.length === 0
          || filas.some((p) => ('pais' in p ? p.pais : null) === 'ESP' || p.club === 'ESP');
      } catch {
        tieneEsp[key] = true; // sin datos aún: no bloquear
      }
    }));
  }
  for (const [, fases] of porEvento) {
    for (const [, us] of fases) {
      const claves = us.map((u) => u.key).filter((k) => k in tieneEsp);
      if (!claves.length) continue;
      const ronda = claves.some((k) => tieneEsp[k]);
      for (const u of us) out[u.key] = ronda;
    }
  }
  return out;
}

/** Atletas ESP de TODA la competición con sus pruebas (feed de inscritos,
 *  campo `entries`), para /fotos-finales — paridad con espAtletasWA.
 *  Individuales dedup por nombre; relevos: una entrada 'Spain' por prueba.
 *  ORDEN DE PARTICIPACIÓN (primera aparición en el timetable), no
 *  alfabético: así se ve qué foto urge subir antes (Santi 2026-08-07). */
export async function espAtletasEA(url: string): Promise<{ nombre: string; evento: string; esRelevo: boolean }[]> {
  const code = await codigoDeUrl(url);
  const [resp, feed] = await Promise.all([
    fetchEventsEA(url),
    trpc<{ athletes?: { athleteId: string; firstName?: string; lastName?: string; countryCode?: string; entries?: { eventId?: string; eventName?: string }[] }[] }>(
      'liveResults.getAthletesFeed', { competitionCode: code }),
  ]);
  // Primera aparición y nombre visible de cada evento (eventId[:22]).
  const primeraTs = new Map<string, string>();
  const nombreEvento = new Map<string, string>();
  for (const e of resp.eventos) {
    const uid = (e.detalleKey ?? '').split(':')[2] ?? '';
    const ev = uid.slice(0, 22).toUpperCase();
    if (!ev) continue;
    const [dd, mm, yy] = (e.fecha ?? '').split('/');
    const ts = `${yy ?? ''}${mm ?? ''}${dd ?? ''}${e.hora ?? ''}`;
    if (!primeraTs.has(ev) || ts < primeraTs.get(ev)!) primeraTs.set(ev, ts);
    if (!nombreEvento.has(ev)) nombreEvento.set(ev, e.nombre);
  }
  const individuales = new Map<string, { nombre: string; evento: string; esRelevo: boolean; ts: string }>();
  const relevos = new Map<string, { nombre: string; evento: string; esRelevo: boolean; ts: string }>();
  for (const a of feed.athletes ?? []) {
    if ((a.countryCode ?? '').trim() !== 'ESP') continue;
    const apellido = (a.lastName ?? '').trim().toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
    const nombre = `${(a.firstName ?? '').trim()} ${apellido}`.trim();
    for (const en of a.entries ?? []) {
      const ev = (en.eventId ?? '').slice(0, 22).toUpperCase();
      if (!ev) continue;
      const evento = nombreEvento.get(ev)
        ?? (en.eventName ?? ev).replace('Women', 'Mujeres').replace('Men', 'Hombres');
      const ts = primeraTs.get(ev) ?? '9999';
      if (ev.includes('4X')) {
        const prev = relevos.get(evento);
        if (!prev || ts < prev.ts) relevos.set(evento, { nombre: 'Spain', evento, esRelevo: true, ts });
      } else {
        const prev = individuales.get(nombre);
        if (!prev || ts < prev.ts) individuales.set(nombre, { nombre, evento, esRelevo: false, ts });
      }
    }
  }
  return [...individuales.values(), ...relevos.values()]
    .sort((a, b) => a.ts.localeCompare(b.ts) || a.nombre.localeCompare(b.nombre, 'es'))
    .map(({ ts: _ts, ...resto }) => resto);
}
